export const AppPermission = {
  ADDRESS_VIEW: 'ADDRESS_VIEW',
  ADDRESS_CREATE: 'ADDRESS_CREATE',
  ADDRESS_EDIT: 'ADDRESS_EDIT',
  TASK_VIEW: 'TASK_VIEW',
  TASK_CREATE: 'TASK_CREATE',
  AUDIT_VIEW: 'AUDIT_VIEW',
  USER_ACCOUNT_MANAGE: 'USER_ACCOUNT_MANAGE',
  ADDRESS_DUPLICATE_MANAGE: 'ADDRESS_DUPLICATE_MANAGE',
  SWEEP_MAINTENANCE_MANAGE: 'SWEEP_MAINTENANCE_MANAGE'
} as const

export type AppPermissionId = typeof AppPermission[keyof typeof AppPermission]

export interface PermissionDefinition {
  id: AppPermissionId
  label: string
  description: string
}

export interface PermissionGroup {
  title: string
  description: string
  permissions: PermissionDefinition[]
}

export const manageablePermissionGroups: PermissionGroup[] = [
  {
    title: 'Kohteet',
    description: 'Asiakas- ja osoitetietojen käyttöoikeudet.',
    permissions: [
      {
        id: AppPermission.ADDRESS_VIEW,
        label: 'Kohteiden katselu',
        description: 'Näkee kohdelistan, asiakastiedot, tulisijat ja nuohouspäivät.'
      },
      {
        id: AppPermission.ADDRESS_CREATE,
        label: 'Kohteiden lisääminen',
        description: 'Voi luoda uusia asiakas- ja osoitekohteita.'
      },
      {
        id: AppPermission.ADDRESS_EDIT,
        label: 'Kohteiden muokkaaminen',
        description: 'Voi muuttaa olemassa olevien kohteiden tietoja.'
      }
    ]
  },
  {
    title: 'Työmerkinnät',
    description: 'Kohteille tallennettujen työmerkintöjen käyttöoikeudet.',
    permissions: [
      {
        id: AppPermission.TASK_VIEW,
        label: 'Työmerkintöjen katselu',
        description: 'Näkee kohteille aiemmin kirjatut työmerkinnät ja niiden tekijät.'
      },
      {
        id: AppPermission.TASK_CREATE,
        label: 'Työmerkintöjen lisääminen',
        description: 'Voi kirjata kohteelle uuden suoritetun työn.'
      }
    ]
  }
]

export const manageablePermissions = manageablePermissionGroups.flatMap(group => group.permissions)

const permissionDependencies: Partial<Record<AppPermissionId, AppPermissionId[]>> = {
  [AppPermission.ADDRESS_CREATE]: [AppPermission.ADDRESS_VIEW],
  [AppPermission.ADDRESS_EDIT]: [AppPermission.ADDRESS_VIEW],
  [AppPermission.TASK_VIEW]: [AppPermission.ADDRESS_VIEW],
  [AppPermission.TASK_CREATE]: [AppPermission.TASK_VIEW, AppPermission.ADDRESS_VIEW]
}

const addDependencies = (permissions: Set<string>): Set<string> => {
  let changed = true
  while (changed) {
    changed = false
    for (const permission of Array.from(permissions)) {
      for (const dependency of permissionDependencies[permission as AppPermissionId] || []) {
        if (!permissions.has(dependency)) {
          permissions.add(dependency)
          changed = true
        }
      }
    }
  }
  return permissions
}

export const togglePermission = (current: string[], permission: AppPermissionId): string[] => {
  const next = new Set(current)

  if (!next.has(permission)) {
    next.add(permission)
    return Array.from(addDependencies(next))
  }

  next.delete(permission)
  let changed = true
  while (changed) {
    changed = false
    for (const selected of Array.from(next)) {
      if ((permissionDependencies[selected as AppPermissionId] || []).some(dependency => !next.has(dependency))) {
        next.delete(selected)
        changed = true
      }
    }
  }

  return Array.from(next)
}

export const hasPermission = (permissions: string[] | undefined, permission: AppPermissionId): boolean => (
  Boolean(permissions?.includes(permission))
)
